import { useState } from 'react';
import { apiClient } from '../../../services/api/client';
import { Organigramme, OrganigrammeDto, OrganigrammeNode, EmployeeHierarchy, EmployeeHierarchyCreateDto, EmployeeSummary } from '../../../types/organisation';

export const useOrganigramme = () => {
  const [organigrammes, setOrganigrammes] = useState<Organigramme[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganigrammes = async (date?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const params: Record<string, string | number> = {};
      if (date) params.date = date;
      
      const data: OrganigrammeDto[] = await apiClient.get('/organisation/organigramme', params);
      // Convertir les DTOs en format legacy pour compatibilité
      const convertedData: Organigramme[] = data.map(dto => ({
        site: dto.metadata.siteId ? {
          id: dto.metadata.siteId,
          siteCode: dto.metadata.siteCode || '',
          siteName: dto.metadata.siteName || '',
          isMainSite: false, // Default value, could be determined from metadata if available
        } : { id: 0, siteCode: '', siteName: '', isMainSite: false },
        nodes: dto.nodes.map(node => ({
          id: node.id,
          type: node.type as OrganigrammeNode['type'],
          label: node.data.employee ? `${node.data.employee.firstName} ${node.data.employee.lastName}` : node.id,
          description: node.data.position?.positionName || '',
          data: node.data,
          color: node.data.isManager ? '#3B82F6' : '#6B7280',
          icon: node.data.isManager ? 'user-tie' : 'user',
        })),
        edges: dto.edges.map(edge => ({
          from: edge.source,
          to: edge.target,
          type: 'reports_to' as const,
          label: edge.data.hierarchyType,
          color: edge.data.isTemporary ? '#F59E0B' : '#10B981',
        })),
        stats: {
          totalEmployees: dto.metadata.totalEmployees,
          totalManagers: dto.metadata.totalManagers,
          totalDepartments: dto.metadata.totalDepartments || 0,
          totalPositions: dto.metadata.totalPositions || 0,
          multiSiteManagers: dto.metadata.multiSiteManagers || 0,
        },
      }));
      setOrganigrammes(convertedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des organigrammes');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganigrammeBySite = async (siteId: number, date?: string): Promise<Organigramme | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const params: Record<string, string | number> = {};
      if (date) params.date = date;
      
      const dto: OrganigrammeDto = await apiClient.get(`/organisation/organigramme/site/${siteId}`, params);
      // Convertir le DTO en format legacy pour compatibilité
      const convertedData: Organigramme = {
        site: dto.metadata.siteId ? {
          id: dto.metadata.siteId,
          siteCode: dto.metadata.siteCode || '',
          siteName: dto.metadata.siteName || '',
          isMainSite: false, // Default value, could be determined from metadata if available
        } : { id: 0, siteCode: '', siteName: '', isMainSite: false },
        nodes: dto.nodes.map(node => ({
          id: node.id,
          type: node.type as OrganigrammeNode['type'],
          label: node.data.employee ? `${node.data.employee.firstName} ${node.data.employee.lastName}` : node.id,
          description: node.data.position?.positionName || '',
          data: node.data,
          color: node.data.isManager ? '#3B82F6' : '#6B7280',
          icon: node.data.isManager ? 'user-tie' : 'user',
        })),
        edges: dto.edges.map(edge => ({
          from: edge.source,
          to: edge.target,
          type: 'reports_to' as const,
          label: edge.data.hierarchyType,
          color: edge.data.isTemporary ? '#F59E0B' : '#10B981',
        })),
        stats: {
          totalEmployees: dto.metadata.totalEmployees,
          totalManagers: dto.metadata.totalManagers,
          totalDepartments: dto.metadata.totalDepartments || 0,
          totalPositions: dto.metadata.totalPositions || 0,
          multiSiteManagers: dto.metadata.multiSiteManagers || 0,
        },
      };
      return convertedData;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement de l\'organigramme');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganigrammeByDepartment = async (departmentId: number, date?: string): Promise<Organigramme | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const params: Record<string, string | number> = {};
      if (date) params.date = date;
      
      const dto: OrganigrammeDto = await apiClient.get(`/organisation/organigramme/department/${departmentId}`, params);
      // Convertir le DTO en format legacy pour compatibilité
      const convertedData: Organigramme = {
        site: dto.metadata.siteId ? {
          id: dto.metadata.siteId,
          siteCode: dto.metadata.siteCode || '',
          siteName: dto.metadata.siteName || '',
          isMainSite: false, // Default value, could be determined from metadata if available
        } : { id: 0, siteCode: '', siteName: '', isMainSite: false },
        nodes: dto.nodes.map(node => ({
          id: node.id,
          type: node.type as OrganigrammeNode['type'],
          label: node.data.employee ? `${node.data.employee.firstName} ${node.data.employee.lastName}` : node.id,
          description: node.data.position?.positionName || '',
          data: node.data,
          color: node.data.isManager ? '#3B82F6' : '#6B7280',
          icon: node.data.isManager ? 'user-tie' : 'user',
        })),
        edges: dto.edges.map(edge => ({
          from: edge.source,
          to: edge.target,
          type: 'reports_to' as const,
          label: edge.data.hierarchyType,
          color: edge.data.isTemporary ? '#F59E0B' : '#10B981',
        })),
        stats: {
          totalEmployees: dto.metadata.totalEmployees,
          totalManagers: dto.metadata.totalManagers,
          totalDepartments: dto.metadata.totalDepartments || 0,
          totalPositions: dto.metadata.totalPositions || 0,
          multiSiteManagers: dto.metadata.multiSiteManagers || 0,
        },
      };
      return convertedData;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement de l\'organigramme');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchMultiSiteManagers = async (date?: string): Promise<EmployeeSummary[]> => {
    setLoading(true);
    setError(null);
    
    try {
      const params: Record<string, string | number> = {};
      if (date) params.date = date;
      
      const data = await apiClient.get('/organisation/organigramme/multi-site-managers', params);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des managers multi-sites');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const assignManager = async (dto: EmployeeHierarchyCreateDto): Promise<EmployeeHierarchy | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiClient.post('/organisation/organigramme/assign-manager', dto);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'assignation du manager');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const endHierarchy = async (hierarchyId: number, endDate: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const params = { endDate };
      await apiClient.put(`/organisation/organigramme/${hierarchyId}/end`, params);

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la fin de la hiérarchie');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeHierarchies = async (employeeId: number, date?: string): Promise<EmployeeHierarchy[]> => {
    setLoading(true);
    setError(null);
    
    try {
      const params: Record<string, string | number> = {};
      if (date) params.date = date;
      
      const data = await apiClient.get(`/organisation/organigramme/employee/${employeeId}`, params);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des hiérarchies de l\'employé');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchManagedEmployees = async (managerId: number, siteId?: number, date?: string): Promise<EmployeeHierarchy[]> => {
    setLoading(true);
    setError(null);
    
    try {
      const params: Record<string, string | number> = {};
      if (siteId) params.siteId = siteId;
      if (date) params.date = date;
      
      const data = await apiClient.get(`/organisation/organigramme/manager/${managerId}`, params);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des employés gérés');
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    organigrammes,
    loading,
    error,
    fetchOrganigrammes,
    fetchOrganigrammeBySite,
    fetchOrganigrammeByDepartment,
    fetchMultiSiteManagers,
    assignManager,
    endHierarchy,
    fetchEmployeeHierarchies,
    fetchManagedEmployees,
  };
};

